import _ from 'lodash';

import * as api from '../../../../api';
import * as logger from '../../../../logger';

import {
  getRequiredInsuranceFields,
  getRequiredSubscriberFields,
} from '../FormDetailsPage/utils';

export async function maybeUpdateInsuranceDetails(
  value: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue,
  ocrResult: api.InsuranceCardOcrResult.InsuranceCardOcrResult | undefined,
  encounterId: string,
) {
  const insuranceCoverages = value?.insuranceCoverages ?? [];
  // temporary batch upload for feature parity
  const toUpdate = insuranceCoverages.map((insuranceCoverage) => {
    const { id, subscriberId } = insuranceCoverage;
    const subscriber = value?.subscribers.find((s) => s.id === subscriberId);
    const updateBody = _.omit(
      {
        ...subscriber,
        ...insuranceCoverage,
      },
      [
        'id',
        'subscriberId',
        'primary',
        'primaryPolicyHolderAddressId',
        'primaryPolicyHolderWorkPhone',
      ],
    );

    const insuranceInformationUpdate =
      api.client.PrevisitPatientInsuranceInformation.update({
        insuranceInformationId: id,
        values: updateBody,
        encounterId,
      }).catch(
        // ignore error throws for now
        (err) => {
          logger.error(
            err,
            'Failed to update insurance information due to error',
          );
        },
      );

    return insuranceInformationUpdate;
  });
  await Promise.all(toUpdate);

  const lastInsuranceCoverage =
    insuranceCoverages[insuranceCoverages.length - 1];
  if (lastInsuranceCoverage) {
    if (ocrResult) {
      await updateOcrResult(ocrResult, lastInsuranceCoverage);
    }
    await predictInsurancePlan(lastInsuranceCoverage);
  }
}

export function getInsuranceErrors(
  cards: api.PatientInsuranceInformation.InsuranceInformationWithDetails[],
  insuranceInformation: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue,
  practicePreferences: {
    enablePrevisitSubscriberToGuarantorRelationshipCollection: boolean;
    collectSubscriberInfo: boolean;
  },
  selectedGuarantor?: api.Guarantor.PrevisitGuarantor,
): Record<string, string> {
  const { insuranceCoverages = [], subscribers } = insuranceInformation;
  const insuranceIdToErrors: Record<string, string> = {};
  const requiredInsuranceKeys = getRequiredInsuranceFields();

  // if we don't collect subscriber info, export relies on uploads
  if (!practicePreferences.collectSubscriberInfo) return {};

  // check that every card is fully represented in form field
  for (const card of cards) {
    const coverage = insuranceCoverages.find(
      (insurance) => insurance.id === card.id,
    );
    if (!coverage) {
      insuranceIdToErrors[card.id] = 'Requires verification';
      continue;
    }

    const needsInsuranceFields = _.some(requiredInsuranceKeys, (key) => {
      const value = coverage[key];
      // don't allow null/undefined
      if (value === null || value === undefined) return true;

      // don't allow empty/blank strings
      if (typeof value === 'string' && !value.trim()) return true;
    });
    if (needsInsuranceFields) {
      insuranceIdToErrors[card.id] = 'Missing insurance information';
      continue;
    }

    if (coverage.subscriberId === coverage.id) {
      // NOTE: ignoring ingested insurance
      continue;
    }

    const subscriber = subscribers.find(
      (subscriber) => subscriber.id === coverage.subscriberId,
    );
    if (!subscriber) {
      insuranceIdToErrors[card.id] = 'Missing subscriber selection';
      continue;
    }

    const requiredSubscriberFields = getRequiredSubscriberFields(
      subscriber,
      practicePreferences,
      selectedGuarantor,
    );

    const needsSubscriberFields = _.some(requiredSubscriberFields, (req) => {
      if (typeof req === 'string') {
        return subscriber[req] === null || subscriber[req] === undefined;
      }

      return !_.some(
        req,
        (key) => subscriber[key] !== null || subscriber[key] !== undefined,
      );
    });

    if (needsSubscriberFields) {
      insuranceIdToErrors[card.id] = 'Missing subscriber information';
    }
  }

  return insuranceIdToErrors;
}

async function updateOcrResult(
  ocrResult: api.InsuranceCardOcrResult.InsuranceCardOcrResult,
  insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage,
) {
  const { coverageStartDate, groupNumber, primaryPolicyHolderMemberId, payor } =
    insuranceCoverage;
  const ocrUpdateBody: api.InsuranceCardOcrResult.UpdateValues = _.omitBy(
    {
      coverageStartDateVerified: coverageStartDate || null,
      groupNumberVerified: groupNumber || null,
      insuranceProviderNameVerified: payor || null,
      memberIdVerified: primaryPolicyHolderMemberId || null,
    },
    _.isNil,
  );

  if (!_.isEmpty(ocrUpdateBody)) {
    await api.client.InsuranceCardOcrResult.update(
      ocrResult.id,
      ocrUpdateBody,
    ).catch((err) => {
      logger.error(err, 'Failed to update insurance ocr results due to error');
    });
  }
}

async function predictInsurancePlan(
  insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage,
) {
  await api.client.PrevisitPatientInsuranceInformation.plan(
    insuranceCoverage.id,
  ).catch((err) => {
    logger.error(err, 'Failed to predict insurance plan due to error');
  });
}
